import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Grid2,
} from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";

const MASTER_INVENTORY_TYPE = 1;

interface CreateInventoryPopupProps {
  open: boolean;
  onClose: () => void;
  onCreate: (data: {
    customerId: number;
    orderId: number;
    type: number;
    label: string;
  }) => void;
  labelInputText?: string;
  orderId: number;
  customerId: number;
}

export default function CreateInventoryPopup({
  open,
  onClose,
  onCreate,
  labelInputText = "Inventory Label",
  orderId,
  customerId,
}: CreateInventoryPopupProps) {
  const theme = useTheme();
  const [inventoryLabel, setInventoryLabel] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!open) {
      setInventoryLabel("");
      setErrorMessage("");
    }
  }, [open]);

  const handleClose = () => {
    try {
      setInventoryLabel("");
      setErrorMessage("");
      onClose();
    } catch (error) {
      console.error("Failed to close create inventory popup:", error);
    }
  };

  const handleCreate = () => {
    try {
      const trimmedLabel = inventoryLabel.trim();
      if (!trimmedLabel) {
        setErrorMessage("Please enter the inventory label.");
        return;
      }

      if (!customerId || !orderId) {
        setErrorMessage("Customer or order details are missing.");
        return;
      }

      onCreate({
        customerId,
        orderId,
        type: MASTER_INVENTORY_TYPE,
        label: trimmedLabel,
      });
      setInventoryLabel("");
      setErrorMessage("");
    } catch (error) {
      console.error("Failed to create inventory:", error);
      setErrorMessage("Something went wrong. Please try again.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "@media (max-width:600px)": {
          width: "100vw",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 4,
          pt: 4,
          pb: 3,
        }}
      >
        <CustomTypography variant="heading4">Create Inventory</CustomTypography>
      </DialogTitle>

      <DialogContent sx={{ px: 4, pt: 1, pb: 3 }}>
        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12 }}>
            <CustomTypography
              variant="heading7"
              color={theme.palette.customColors.greyTextColor}
              style={{ marginBottom: 14 }}
            >
              Inventory Label
            </CustomTypography>
            <CustomTextField
              borderColor={`0.1px solid ${theme.palette.customColors.greyDarkborder}`}
              value={inventoryLabel}
              placeholder={labelInputText}
              width="100%"
              type="text"
              onChange={(e) => {
                setInventoryLabel(e.target.value);
                if (errorMessage) setErrorMessage("");
              }}
            />
            <CustomTypography
              variant="heading7"
              color={theme.palette.customColors.greyTextColor}
              sx={{ mt: 1.5 }}
            >
              This will create a Master Inventory for the customer and map this
              order to it.
            </CustomTypography>
            {errorMessage ? (
              <CustomTypography
                variant="heading7"
                color={theme.palette.error.main}
                sx={{ mt: 1 }}
              >
                {errorMessage}
              </CustomTypography>
            ) : null}
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 3, justifyContent: "flex-end", gap: 1 }}>
        <AppButton
          onClick={handleClose}
          label="Cancel"
          fontWeight={600}
          customStyles={{
            height: "2.2rem",
            minWidth: "5.438rem",
            borderRadius: "4px",
            border: `1px solid ${theme.palette.customColors.greyTextColor}`,
            backgroundColor: theme.palette.common.white,
            color: theme.palette.customColors.greyTextColor,
          }}
          variant="grayscale"
        />
        <AppButton
          label="Create"
          onClick={handleCreate}
          fontWeight={600}
          customStyles={{
            height: "2.2rem",
            minWidth: "5.438rem",
            borderRadius: "4px",
            backgroundColor: theme.palette.customColors.pinkDark,
            color: theme.palette.common.white,
          }}
          variant="secondary"
        />
      </DialogActions>
    </Dialog>
  );
}
